import get from 'lodash/get';
import { fulfillmentStatuses } from '../constants';

export const DEFAULT_ENTITLEMENTS_ENDPOINT = '/entitlements';
export const DEFAULT_ENTITLEMENTS_POLLING_INTERVAL_MS = 5000;
export const DEFAULT_ENTITLEMENTS_MAX_TIMEOUT_MS = 30000;

// eslint-disable-next-line no-promise-executor-return
const awaitableTimeout = ms => new Promise(resolve => setTimeout(resolve, ms));

export const isEntitlementsComplete = response => {
  let responseItems = [];

  if (Array.isArray(response)) {
    responseItems = response;
  } else if (Array.isArray(response?.data)) {
    responseItems = response.data;
  } else {
    responseItems = [response?.data || response];
  }

  return (
    response?.data?.response === true ||
    response?.response === true ||
    responseItems.some(
      item =>
        item?.response === true ||
        item?.status === true ||
        !!item?.subscriptionId ||
        item?.fulfillmentStatus === fulfillmentStatuses.succeeded
    ) ||
    !!get(response, 'data.subscriptions[0].subscriptionId', false)
  );
};

export default async function pollEntitlementsUntilComplete({
  dispatch,
  fetchAction,
  endpoint = DEFAULT_ENTITLEMENTS_ENDPOINT,
  pollingIntervalMs = DEFAULT_ENTITLEMENTS_POLLING_INTERVAL_MS,
  maxTimeoutMs = DEFAULT_ENTITLEMENTS_MAX_TIMEOUT_MS,
  initialDelayMs = 0,
  currentPlanCode = null,
  shouldStop = () => false,
  onRetryError,
  onTimeout,
  onComplete,
  onStopped,
}) {
  if (initialDelayMs > 0) {
    await awaitableTimeout(initialDelayMs);
  }

  const startTime = Date.now();
  let attempts = 0;

  /* eslint-disable no-await-in-loop */
  while (!shouldStop() && Date.now() - startTime < maxTimeoutMs) {
    attempts += 1;

    try {
      const response = await dispatch(
        fetchAction(endpoint, {
          method: 'GET',
        })
      );

      // When a currentPlanCode is supplied, only treat the response as complete
      // once the entitlements planCode has changed — confirming the backend has
      // applied the new offer rather than still reflecting the old subscription state.
      const responsePlanCode = response?.planCode ?? response?.data?.planCode;
      const hasPlanChanged = !currentPlanCode || responsePlanCode !== currentPlanCode;
      // defermentInfo signals a pending activation — plan change check is not applicable
      const isReadyToComplete = !!response?.defermentInfo || hasPlanChanged;

      if (isReadyToComplete && isEntitlementsComplete(response)) {
        onComplete?.({ attempts, elapsedMs: Date.now() - startTime });
        return true;
      }
    } catch (error) {
      // Keep polling until timeout to tolerate transient API failures.
      onRetryError?.({ attempts, elapsedMs: Date.now() - startTime, error });
    }

    if (shouldStop()) {
      onStopped?.({ attempts, elapsedMs: Date.now() - startTime });
      return false;
    }

    await awaitableTimeout(pollingIntervalMs);
  }
  /* eslint-enable no-await-in-loop */

  onTimeout?.({ attempts, elapsedMs: Date.now() - startTime });

  return false;
}
